/**
 * Phase 6 — My Signals — PRD §5.6
 *
 * Workstream-filtered personal curation view with inline markdown expansion.
 * Workstream filter bar uses OR logic; keyword search covers title/source/wikilinks.
 * Clicking a signal entry expands it inline, rendering the full signal as markdown.
 * Expanded entries show Edit (→ Folder View) and Delete buttons.
 */

import { api, ApiError } from "../lib/api";
import { getToken } from "../lib/auth";
import { navigate } from "../lib/router";
import { renderHeader } from "../components/header";
import { attachNoteEditor } from "../lib/note_editor";

// Ambient declarations for CDN libraries (index.html)
declare const marked: { parse: (md: string) => string };
declare const DOMPurify: { sanitize: (html: string) => string };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SteepleScores {
  social: number;
  technological: number;
  economic: number;
  environmental: number;
  political: number;
  legal: number;
  ethical: number;
  security: number;
}

interface Signal {
  id: string;
  title: string;
  source: string;
  sourceUrl: string;
  datePublished: string;
  dateAdded: string;
  workstreams: string[];
  steeples: SteepleScores;
  wikilinks: string[];
  summary: string;
  keyImplications: string[];
  rawExcerpts?: string;
  createdAt: string;
  updatedAt: string;
  commentCount: number;
}

interface Workstream {
  id: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Module state (reset on each page render)
// ---------------------------------------------------------------------------

let _allSignals: Signal[] = [];
let _allWorkstreams: Workstream[] = [];
let _selectedWorkstreamIds: Set<string> = new Set();
let _keyword = "";
let _expandedId: string | null = null;
let _listWrap: HTMLElement | null = null;
let _wsBar: HTMLElement | null = null;
let _debounceTimer: ReturnType<typeof setTimeout> | null = null;

/** Set a keyword to pre-fill on the next page render (consumed once). */
let _pendingKeyword: string | null = null;
export function presetKeyword(q: string): void { _pendingKeyword = q; }

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function escHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function wsName(id: string): string {
  return _allWorkstreams.find((w) => w.id === id)?.name ?? id;
}

/** Return the 3 highest-scoring STEEPLES dimensions */
function top3Steeples(s: SteepleScores): Array<{ label: string; score: number }> {
  const dims: Array<[string, number]> = [
    ["Soc", s.social],
    ["Tech", s.technological],
    ["Eco", s.economic],
    ["Env", s.environmental],
    ["Pol", s.political],
    ["Leg", s.legal],
    ["Eth", s.ethical],
    ["Sec", s.security],
  ];
  return dims
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([label, score]) => ({ label, score }));
}

/** Build full markdown from a signal (matches export template — PRD §3.7) */
function signalToMarkdown(sig: Signal): string {
  const wsNames = sig.workstreams.map(wsName).join(", ");
  const ki = sig.keyImplications.map((i) => `- ${i}`).join("\n");
  const tags = sig.wikilinks.map((w) => `#${w}`).join(" ");
  const steeplesMd = [
    "| Dimension | Score |",
    "|---|---|",
    `| Social | ${sig.steeples.social}/10 |`,
    `| Technological | ${sig.steeples.technological}/10 |`,
    `| Economic | ${sig.steeples.economic}/10 |`,
    `| Environmental | ${sig.steeples.environmental}/10 |`,
    `| Political | ${sig.steeples.political}/10 |`,
    `| Legal | ${sig.steeples.legal}/10 |`,
    `| Ethical | ${sig.steeples.ethical}/10 |`,
    `| Security | ${sig.steeples.security}/10 |`,
  ].join("\n");

  let md = `# ${sig.title}\n\n`;
  md += `**Source:** [${sig.source}](${sig.sourceUrl})  \n`;
  md += `**Published:** ${sig.datePublished} | **Added:** ${sig.dateAdded}\n\n`;
  if (wsNames) md += `**Workstreams:** ${wsNames}\n\n`;
  md += `## STEEPLES Scores\n\n${steeplesMd}\n\n`;
  md += `## Summary\n\n${sig.summary}\n\n`;
  if (sig.keyImplications.length) md += `## Key Implications\n\n${ki}\n\n`;
  if (sig.wikilinks.length) md += `## Wikilinks\n\n${tags}\n\n`;
  if (sig.rawExcerpts) md += `## Raw Excerpts\n\n${sig.rawExcerpts}\n`;
  return md;
}

async function downloadSignalMd(signalId: string): Promise<void> {
  const token = getToken();
  try {
    const res = await fetch(`/api/signals/${signalId}/export`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `signal-${signalId.slice(0, 8)}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    alert(`Download failed: ${err instanceof Error ? err.message : String(err)}`);
  }
}

// ---------------------------------------------------------------------------
// Client-side filtering
// ---------------------------------------------------------------------------

function filterSignals(): Signal[] {
  let result = _allSignals;

  // Workstream filter — OR logic (PRD §6.5)
  if (_selectedWorkstreamIds.size > 0) {
    result = result.filter((s) =>
      s.workstreams.some((id) => _selectedWorkstreamIds.has(id))
    );
  }

  // Keyword filter — title, source, wikilinks (PRD §5.6)
  if (_keyword) {
    const q = _keyword.toLowerCase();
    result = result.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.source.toLowerCase().includes(q) ||
        s.wikilinks.some((w) => w.toLowerCase().includes(q))
    );
  }

  return result;
}

// ---------------------------------------------------------------------------
// Render signal list
// ---------------------------------------------------------------------------

function renderList(): void {
  if (!_listWrap) return;
  const filtered = filterSignals();

  _listWrap.innerHTML = "";

  const count = document.createElement("p");
  count.className = "ms-count";
  count.textContent = `${filtered.length} signal${filtered.length === 1 ? "" : "s"}`;
  _listWrap.appendChild(count);

  if (filtered.length === 0) {
    const empty = document.createElement("div");
    empty.className = "ms-empty";
    empty.textContent =
      _allSignals.length === 0
        ? "No signals collected yet."
        : "No signals match the current filters.";
    _listWrap.appendChild(empty);
    return;
  }

  for (const sig of filtered) {
    _listWrap.appendChild(buildEntry(sig));
  }
}

function buildEntry(sig: Signal): HTMLElement {
  const entry = document.createElement("div");
  entry.className = "ms-entry" + (sig.id === _expandedId ? " expanded" : "");
  entry.dataset["signalId"] = sig.id;

  // Workstream pills
  const pills = sig.workstreams
    .map((id) => `<span class="ms-ws-pill">${escHtml(wsName(id))}</span>`)
    .join("");

  // Top 3 STEEPLES compact badges
  const top3 = top3Steeples(sig.steeples);
  const steeplesBadges = top3
    .map(
      ({ label, score }) =>
        `<span class="ms-steeples-badge${score >= 6 ? " high" : ""}">${label}: ${score}</span>`
    )
    .join("");

  entry.innerHTML = `
    <div class="ms-entry-header">
      <span class="ms-entry-chevron">▶</span>
      <div class="ms-entry-main">
        <div class="ms-entry-title">${escHtml(sig.title)}</div>
        <div class="ms-entry-meta">${escHtml(sig.source)} · ${escHtml(sig.datePublished)}</div>
        ${pills ? `<div class="ms-entry-pills">${pills}</div>` : ""}
        <div class="ms-steeples-row">
          ${steeplesBadges}
          <span class="ms-comment-count">Comments: ${sig.commentCount ?? 0}</span>
        </div>
      </div>
      <div class="ms-entry-aside">
        <button class="ms-dl-btn icon-btn" title="Download as markdown">⬇</button>
      </div>
    </div>
    <div class="ms-entry-body">
      <div class="ms-entry-content"></div>
      <div class="ms-entry-edit-row">
        <button class="primary ms-edit-btn">Edit</button>
        <button class="destructive ms-delete-btn">Delete</button>
      </div>
    </div>`;

  // Toggle expand on header click
  const headerEl = entry.querySelector<HTMLDivElement>(".ms-entry-header")!;
  headerEl.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).closest(".ms-dl-btn")) return;
    toggleExpand(sig, entry);
  });

  // Download button
  const dlBtn = entry.querySelector<HTMLButtonElement>(".ms-dl-btn")!;
  dlBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    void downloadSignalMd(sig.id);
  });

  // Edit → navigate to Folder View with signal pre-selected
  const editBtn = entry.querySelector<HTMLButtonElement>(".ms-edit-btn")!;
  editBtn.addEventListener("click", () => navigate(`/folders?signal=${sig.id}`));

  // Delete
  const deleteBtn = entry.querySelector<HTMLButtonElement>(".ms-delete-btn")!;
  deleteBtn.addEventListener("click", () => openDeleteModal(sig));

  // If already expanded (re-render after delete), fill content immediately
  if (sig.id === _expandedId) {
    fillExpandedContent(sig, entry);
  }

  return entry;
}

function toggleExpand(sig: Signal, entry: HTMLElement): void {
  const isExpanded = entry.classList.contains("expanded");
  if (isExpanded) {
    entry.classList.remove("expanded");
    _expandedId = null;
  } else {
    // Collapse the currently-expanded entry if there is one
    if (_expandedId) {
      _listWrap
        ?.querySelector<HTMLElement>(`[data-signal-id="${_expandedId}"]`)
        ?.classList.remove("expanded");
    }
    _expandedId = sig.id;
    entry.classList.add("expanded");
    fillExpandedContent(sig, entry);
  }
}

function fillExpandedContent(sig: Signal, entry: HTMLElement): void {
  const contentEl = entry.querySelector<HTMLDivElement>(".ms-entry-content")!;
  const md = signalToMarkdown(sig);
  if (typeof marked !== "undefined") {
    const html = marked.parse(md) as string;
    contentEl.innerHTML = typeof DOMPurify !== "undefined" ? DOMPurify.sanitize(html) : html;
  } else {
    contentEl.textContent = md;
  }

  // Mount comment thread below the signal markdown
  const noteContainer = document.createElement("div");
  noteContainer.className = "ms-note-container";
  contentEl.after(noteContainer);

  // Find the count badge in the entry header and keep it in sync
  const countEl = entry.querySelector<HTMLElement>(".ms-comment-count");
  attachNoteEditor(sig.id, noteContainer, (count) => {
    if (countEl) countEl.textContent = `Comments: ${count}`;
  });
}

// ---------------------------------------------------------------------------
// Delete confirmation modal
// ---------------------------------------------------------------------------

function openDeleteModal(sig: Signal): void {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.innerHTML = `
    <div class="modal-box" style="max-width:420px">
      <div class="modal-header">
        <h3>Delete Signal?</h3>
        <button class="icon-btn modal-close">✕</button>
      </div>
      <div class="modal-body">
        <p>Are you sure you want to delete <strong>${escHtml(sig.title)}</strong>?</p>
        <p class="muted" style="font-size:13px;">This action cannot be undone.</p>
        <p class="modal-err muted" id="ms-del-err"></p>
      </div>
      <div class="modal-footer">
        <button class="primary destructive" id="ms-del-confirm">Delete</button>
        <button id="ms-del-cancel">Cancel</button>
      </div>
    </div>`;

  const close = (): void => { overlay.remove(); };
  overlay.querySelector(".modal-close")!.addEventListener("click", close);
  overlay.querySelector("#ms-del-cancel")!.addEventListener("click", close);
  overlay.addEventListener("mousedown", (e) => { if (e.target === overlay) close(); });

  overlay.querySelector("#ms-del-confirm")!.addEventListener("click", async () => {
    const errEl = overlay.querySelector<HTMLParagraphElement>("#ms-del-err")!;
    try {
      await api(`/signals/${sig.id}`, { method: "DELETE" });
      close();
      _allSignals = _allSignals.filter((s) => s.id !== sig.id);
      if (_expandedId === sig.id) _expandedId = null;
      renderList();
    } catch (err) {
      errEl.textContent = err instanceof ApiError ? err.message : "Delete failed.";
    }
  });

  document.body.appendChild(overlay);
}

// ---------------------------------------------------------------------------
// Workstream filter bar
// ---------------------------------------------------------------------------

function renderWsBar(): void {
  if (!_wsBar) return;
  _wsBar.innerHTML = "";

  // "All" button
  const allBtn = document.createElement("button");
  allBtn.className = "ms-ws-btn" + (_selectedWorkstreamIds.size === 0 ? " active" : "");
  allBtn.textContent = "All";
  allBtn.addEventListener("click", () => {
    _selectedWorkstreamIds.clear();
    renderWsBar();
    renderList();
  });
  _wsBar.appendChild(allBtn);

  // One button per workstream
  for (const ws of _allWorkstreams) {
    const btn = document.createElement("button");
    btn.className = "ms-ws-btn" + (_selectedWorkstreamIds.has(ws.id) ? " active" : "");
    btn.textContent = ws.name;
    btn.addEventListener("click", () => {
      if (_selectedWorkstreamIds.has(ws.id)) {
        _selectedWorkstreamIds.delete(ws.id);
      } else {
        _selectedWorkstreamIds.add(ws.id);
      }
      renderWsBar();
      renderList();
    });
    _wsBar.appendChild(btn);
  }
}

// ---------------------------------------------------------------------------
// Page entry point
// ---------------------------------------------------------------------------

export async function renderMySignalsPage(root: HTMLElement): Promise<void> {
  // Reset module state for fresh render
  _allSignals = [];
  _allWorkstreams = [];
  _selectedWorkstreamIds = new Set();
  _keyword = "";
  _expandedId = null;
  _listWrap = null;
  _wsBar = null;
  if (_debounceTimer) {
    clearTimeout(_debounceTimer);
    _debounceTimer = null;
  }

  // Consume any keyword pre-set by another page (e.g. Pulse)
  if (_pendingKeyword) { _keyword = _pendingKeyword; _pendingKeyword = null; }

  root.appendChild(renderHeader());

  const page = document.createElement("div");
  page.className = "ms-page";

  // Toolbar
  const toolbar = document.createElement("div");
  toolbar.className = "ms-toolbar";

  _wsBar = document.createElement("div");
  _wsBar.className = "ms-ws-bar";
  _wsBar.innerHTML = `<span style="font-size:12px;color:var(--text-muted)">Loading…</span>`;

  const searchRow = document.createElement("div");
  searchRow.className = "ms-search-row";

  const searchInput = document.createElement("input");
  searchInput.type = "text";
  searchInput.className = "ms-search-input";
  searchInput.placeholder = "Search by title, source, or wikilink…";
  searchInput.setAttribute("aria-label", "Keyword search");
  searchInput.value = _keyword; // reflect any pre-filled value
  searchInput.addEventListener("input", () => {
    _keyword = searchInput.value;
    if (_debounceTimer) clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(() => renderList(), 300);
  });
  searchRow.appendChild(searchInput);

  toolbar.appendChild(_wsBar);
  toolbar.appendChild(searchRow);

  // Signal list
  _listWrap = document.createElement("div");
  _listWrap.className = "ms-list-wrap";
  _listWrap.innerHTML = `<div class="ms-loading">Loading signals…</div>`;

  page.appendChild(toolbar);
  page.appendChild(_listWrap);
  root.appendChild(page);

  // Load signals + workstreams in parallel
  try {
    const [signals, workstreams] = await Promise.all([
      api<Signal[]>("/search"),
      api<Workstream[]>("/workstreams"),
    ]);
    _allSignals = signals;
    _allWorkstreams = workstreams;
    renderWsBar();
    renderList();
  } catch (err) {
    if (_listWrap) {
      _listWrap.innerHTML = `<div class="ms-empty">Failed to load signals.${err instanceof ApiError ? " " + err.message : ""}</div>`;
    }
    if (_wsBar) _wsBar.innerHTML = "";
  }
}
